/**
 * Bleed detection - pure. Did we measure a voice, or did we measure the speakers?
 *
 * §12.2: Level 1 is the only mode in this Epic where the reference sounds while the microphone is
 * open, which is exactly the condition §5's headphone gate exists to prevent. The gate is a
 * checkbox, and a checkbox is a promise rather than a measurement - so the take is measured too.
 *
 * Both conditions are required, and both are quantified against §5.6's own numbers, because the
 * only claim this function is entitled to make is "no voice can do this". Handing a singer a fake
 * perfect score is the worst outcome available to a tool whose entire output is a measurement of
 * them; refusing to score a good singer is the second worst, and the conjunction is what keeps the
 * second from happening.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §12.2
 */

/**
 * Every paired note within this many cents of the reference.
 *
 * 5 cents is half the melodic just-noticeable difference (`fullScoreCents` = 10, vocal §5.4). A
 * singer does not hold half a JND across a whole phrase; a tracker following a soundfont does,
 * because it is following the soundfont.
 */
export const bleedCentsMax = 5

/**
 * ...and every paired note steadier than this interquartile range, in cents.
 *
 * `stabilityIqrCents` (vocal §5.6) is 90, and the reasoning behind it puts a modest pop vibrato at
 * an IQR of 42 and a healthy classical one near 80. 10 cents is an order of magnitude under the
 * steadiest healthy voice and equal to the melodic JND: it is the width of a sustained synthesised
 * tone, not of anybody's singing.
 */
export const bleedIqrMax = 10

/** Only the deviation is read; this is `PhraseScore['absolute']['perNote']` structurally. */
export interface BleedNote {
  /** Signed cents from the reference pitch. */
  cents: number
}

/**
 * True only when EVERY paired note is inside both bounds.
 *
 * @param perNote per-paired-note deviation from the reference, from `scorePhrase`.
 * @param stability per-paired-note IQR in cents, index-aligned with `perNote`.
 */
export const detectBleed = (perNote: BleedNote[], stability: number[]): boolean => {
  /*
   * `every` over an empty array is true, so without this guard a take in which nothing was heard -
   * the one case with no evidence in it at all - would report the strongest verdict available.
   * Two lengths that disagree mean the caller paired something this function cannot read, which is
   * the same absence of evidence.
   */
  if (perNote.length === 0 || perNote.length !== stability.length) {
    return false
  }
  return perNote.every((note, index) => Math.abs(note.cents) <= bleedCentsMax && stability[index] < bleedIqrMax)
}
